import React, { useCallback, useEffect, useState } from "react";

import Drawer, { DrawerProps } from "antd/es/drawer";
import cn from "classnames";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import styles from "./ResizableDrawer.module.scss";
const ON_RESIZE_DEBOUNCE_TIMER = 100;

interface Props extends DrawerProps {
  defaultWidth?: number;
  disableResize?: boolean;
  maxWidth?: number;
  onResize?: (width: number) => void;
}

const ResizableDrawer: React.FC<Props> = ({
  children,
  defaultWidth,
  disableResize,
  maxWidth,
  onResize,
  zIndex,
  ...props
}) => {
  const [isResizing, startResizing, stopResizing] = useBoolean(false);
  // this will set default width at 256px (defined by antD) or value provided,
  // for record panes we pass a className width min-width
  const [width, setWidth] = useState<number | undefined>(defaultWidth);

  const handleOnResize = useDebouncedCallback([
    useCallback((width: number) => onResize?.(width), [onResize]),
    ON_RESIZE_DEBOUNCE_TIMER,
  ]);

  const handleMouseMove = useCallback(
    (e) => {
      if (isResizing) {
        const offsetRight = document.body.offsetWidth - (e.clientX - document.body.offsetLeft);

        setWidth(offsetRight);
        handleOnResize(offsetRight);
      }
    },
    [isResizing, handleOnResize]
  );

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopResizing);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
    };
  }, [handleMouseMove, stopResizing]);

  return (
    <Drawer
      width={width}
      {...props}
      contentWrapperStyle={{
        ...props.contentWrapperStyle,
        maxWidth: `calc(min(99vw, ${maxWidth}px)`,
        // no width animations when resizing
        transitionProperty: isResizing ? "none" : "all",
        width: disableResize ? "100%" : width,
      }}
      zIndex={zIndex}
    >
      {!disableResize && (
        <div
          className={cn(styles.dragger, {
            [styles.maxWidth]: width && maxWidth ? width >= maxWidth : false,
          })}
          onMouseDown={startResizing}
        />
      )}
      {children}
    </Drawer>
  );
};

export default ResizableDrawer;
